import {
  type AssessmentSourceMode,
  buildProductAssessmentScoreSource,
  buildProductAssessmentStatusSource,
  configuredAssessmentSourceModes,
  getLocalizedValue,
  hasScoreSource,
  isQuizScoreSource,
  parseProductAssessmentSource,
  pickT,
  productAssessmentSourceStatuses,
  QUIZ_SCORE_SOURCE_PREFIX,
  quizIdFromScoreSource,
  quizResultStatusLabel,
} from "@repo/shared";
import type {
  ConfiguredAssessmentType,
  FlowQuizSummary,
  QuizResultStatus,
} from "@repo/shared/types";
import { STATUS_T, T } from "./assessment-picker-entries.i18n";

export type AssessmentPickerEntry = {
  /** The condition source id, `__pa_<productTypeId>_<mode>_{status|score}`. */
  id: string;
  /** `Bonds · Quiz · Status`, or `Bonds · Simple` for a simple type. */
  label: string;
  /** Heading the entries are listed under. */
  group: string;
  /** A status compares against `valueOptions`; a score against a number. */
  kind: "status" | "score";
  valueOptions: { value: string; label: string }[];
  /** Score entries only — the mode's pass bar, in points, shown as a hint. */
  passScore?: number;
};

export type QuizScorePickerEntry = {
  /** `__quiz_score_<quizComponentId>` */
  id: string;
  /** `Quiz: Listed Equities · Score` */
  label: string;
  passScore: number;
};

type AssessmentPickerLanguages = {
  /** Group heading and the mode / measure words. */
  uiLanguage: string;
  /** Product type names. */
  nameLanguage: string;
  /** Fallback for product type names — the tenant's default language. */
  fallbackLanguage: string;
  /** Status value labels. */
  valueLanguage: string;
};

/**
 * Value options of a product-assessment source — Passed / Not passed / Not
 * answered, Incomplete for Combined, or Yes / No for a simple type. Keyed by the id alone, so a stored rule
 * whose type is no longer configured still renders its value.
 */
export function assessmentSourceValueOptions(
  sourceFieldId: string,
  valueLanguage: string,
): { value: string; label: string }[] {
  const labels = pickT(STATUS_T, valueLanguage);
  return productAssessmentSourceStatuses(sourceFieldId).map((status) => ({
    value: status,
    label: labels[status],
  }));
}

/**
 * Value options of a quiz-result source (QL-13) — Not answered / Passed / Not
 * passed. Rules compare the raw status string; the labels are for humans only.
 */
export function quizResultValueOptions(valueLanguage: string): { value: string; label: string }[] {
  return (["unanswered", "passed", "failed"] as const satisfies QuizResultStatus[]).map(
    (status) => ({ value: status, label: quizResultStatusLabel(status, valueLanguage) }),
  );
}

/**
 * Per configured mode of each type a Status entry, followed — for Quiz and
 * Questionnaire — by a Score entry. Quiz only when the type has a quiz,
 * Questionnaire only when it has a questionnaire, Combined (status only: the
 * two scores are never added together) only when it has both. A simple type
 * (neither) gets one `<Type> · Simple` entry with Yes / No (QL-541).
 * `configuredTypes` already excludes inactive types.
 */
export function buildAssessmentPickerEntries(
  configuredTypes: ConfiguredAssessmentType[],
  { uiLanguage, nameLanguage, fallbackLanguage, valueLanguage }: AssessmentPickerLanguages,
): AssessmentPickerEntry[] {
  const t = pickT(T, uiLanguage);
  const modeLabels: Record<AssessmentSourceMode, string> = {
    quiz: t.quiz,
    questionnaire: t.questionnaire,
    combined: t.combined,
    simple: t.simple,
  };

  return configuredTypes.flatMap((type) => {
    const typeName = getLocalizedValue(type.nameI18n, nameLanguage, fallbackLanguage);
    return configuredAssessmentSourceModes(type).flatMap((mode): AssessmentPickerEntry[] => {
      const statusId = buildProductAssessmentStatusSource(type.id, mode);
      const status: AssessmentPickerEntry = {
        id: statusId,
        // Simple is a Yes / No question — no "· Status" suffix.
        label:
          mode === "simple"
            ? `${typeName} · ${modeLabels[mode]}`
            : `${typeName} · ${modeLabels[mode]} · ${t.status}`,
        group: t.group,
        kind: "status",
        valueOptions: assessmentSourceValueOptions(statusId, valueLanguage),
      };
      if (!hasScoreSource(mode)) return [status];
      const passScore = type[mode]?.passScore;
      if (passScore === undefined) return [status];
      return [
        status,
        {
          id: buildProductAssessmentScoreSource(type.id, mode),
          label: `${typeName} · ${modeLabels[mode]} · ${t.score}`,
          group: t.group,
          kind: "score",
          valueOptions: [],
          passScore,
        },
      ];
    });
  });
}

/** One `Quiz: <name> · Score` entry per quiz on the flow, next to its status entry. */
export function buildQuizScorePickerEntries(
  quizzes: FlowQuizSummary[],
  uiLanguage: string,
): QuizScorePickerEntry[] {
  const t = pickT(T, uiLanguage);
  return quizzes.map((quiz) => ({
    id: `${QUIZ_SCORE_SOURCE_PREFIX}${quiz.id}`,
    label: `${t.quizField} ${quiz.name} · ${t.score}`,
    passScore: quiz.minScore,
  }));
}

/**
 * The pass score behind a score source, for the hint beside the number input.
 * Keyed by the id alone so a STORED rule shows its hint too; null when the
 * type, mode or quiz is gone.
 */
export function scoreSourcePassScore(
  sourceFieldId: string,
  configuredTypes: ConfiguredAssessmentType[],
  quizzes: FlowQuizSummary[],
): number | null {
  if (isQuizScoreSource(sourceFieldId)) {
    const quizId = quizIdFromScoreSource(sourceFieldId);
    return quizzes.find((quiz) => quiz.id === quizId)?.minScore ?? null;
  }
  const parsed = parseProductAssessmentSource(sourceFieldId);
  if (
    !parsed ||
    parsed.measure !== "score" ||
    parsed.mode === "combined" ||
    parsed.mode === "simple"
  ) {
    return null;
  }
  const type = configuredTypes.find((t) => t.id === parsed.productTypeId);
  return type?.[parsed.mode]?.passScore ?? null;
}
